import React, { useState } from 'react';
import { X, Calendar } from 'lucide-react';
import Button from '../shared/Button';
import DatePicker from './DatePicker';
import { useRequestEventDateChange } from '../../services/bookingDateChangeService';

const EventDateChangeRequestModal = ({
  isOpen,
  onClose,
  bookingId,
  currentEventDate,
  onSuccess,
}) => {
  const [requestedDate, setRequestedDate] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [error, setError] = useState('');

  const mutation = useRequestEventDateChange({
    onSuccess: () => {
      setRequestedDate('');
      setClientNote('');
      setError('');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.requested_event_date?.[0] ||
          'Failed to submit request'
      );
    },
  });

  if (!isOpen) return null;

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().slice(0, 10);

  const formatDisplayDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!requestedDate) {
      setError('Please select a new event date.');
      return;
    }
    mutation.mutate({
      bookingId,
      requested_event_date: requestedDate,
      client_note: clientNote.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg max-w-md w-full relative shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-5 h-5 text-indigo-700" />
            <h2 className="text-lg font-semibold text-gray-900">Request a new event date</h2>
          </div>
          <p className="text-sm text-gray-600">
            Your vendor will review this request. The booking date changes only after they approve it.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Current event date</p>
            <p className="text-sm font-semibold text-black">{formatDisplayDate(currentEventDate)}</p>
          </div>

          <div>
            <DatePicker
              label="New event date *"
              value={requestedDate}
              onChange={(value) => {
                setRequestedDate(value);
                if (error) setError('');
              }}
              placeholder="Select new event date"
              minDate={minDateStr}
              allowPastDates={false}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note to vendor (optional)
            </label>
            <textarea
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Share a short reason, for example a venue or guest conflict..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-black text-sm"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
              Send request
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventDateChangeRequestModal;
